import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment/moment';
import { SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ERole } from '../../../../shared/enums/user/ERole';
import {
  ELeaveDaysStatus,
  ELeaveDaysType,
} from '../../../../shared/enums/leave-days/ELeaveDays';
import { ILeaveDay } from '../../../../shared/interfaces/leave-days/ILeaveDay';
import { RootState } from '../../../../store/store';
import i18n from '../../../../utils/localization/i18n';

interface IProps {
  leaveDay: ILeaveDay;
  setLeaveDay: React.Dispatch<SetStateAction<ILeaveDay | null>>;
}

const LeaveDayFormModalBody = ({ leaveDay, setLeaveDay }: IProps) => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.auth);
  const isNormalUser = userData?.role === ERole.USER;
  return (
    <Box sx={{ marginTop: '30px', marginBottom: '30px', width: '100%' }}>
      <FormControl fullWidth sx={{ mb: '20px' }}>
        <InputLabel id="status-type-label">
          {t('general.leaveDayType')}
        </InputLabel>
        <Select
          labelId="status-type-label"
          id="status-type-select"
          value={leaveDay?.leaveDayType ?? ''}
          label={t('general.leaveDayType')}
          onChange={(e) => {
            setLeaveDay(
              (prev) =>
                ({ ...prev, leaveDayType: e?.target?.value }) as ILeaveDay,
            );
          }}
        >
          {Object.values(ELeaveDaysType)?.map((type) => {
            return (
              <MenuItem key={type} value={type}>
                {t(`leaveDays.leaveDayType.${type}`)}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ width: '100%' }}>
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          adapterLocale={i18n.language}
        >
          <DemoContainer components={['DatePicker']}>
            <DatePicker
              sx={{ width: '100%' }}
              label={t('general.startDate')}
              format="DD.MM.YYYY"
              value={moment(leaveDay?.startDate)}
              onChange={(newDate) => {
                setLeaveDay(
                  (prev) =>
                    ({
                      ...prev,
                      startDate: moment(newDate)?.toISOString(),
                    }) as ILeaveDay,
                );
              }}
            />
          </DemoContainer>
        </LocalizationProvider>
      </FormControl>
      <Box sx={{ marginTop: '20px' }}>
        <FormControl fullWidth sx={{ width: '100%' }}>
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale={i18n.language}
          >
            <DemoContainer components={['DatePicker']}>
              <DatePicker
                sx={{ width: '100%' }}
                label={t('general.endDate')}
                value={moment(leaveDay?.endDate)}
                format="DD.MM.YYYY"
                onChange={(newDate) => {
                  setLeaveDay(
                    (prev) =>
                      ({
                        ...prev,
                        endDate: moment(newDate)?.toISOString(),
                      }) as ILeaveDay,
                  );
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
        </FormControl>
      </Box>
      {!!leaveDay?.id && !isNormalUser && (
        <FormControl fullWidth sx={{ marginTop: '20px' }}>
          <InputLabel id="status-type-label">{t('general.status')}</InputLabel>
          <Select
            labelId="status-type-label"
            id="status-type-select"
            value={leaveDay?.status ?? ''}
            label={t('general.status')}
            onChange={(e) => {
              setLeaveDay(
                (prev) => ({ ...prev, status: e?.target?.value }) as ILeaveDay,
              );
            }}
          >
            {[
              ELeaveDaysStatus.ACCEPTED,
              ELeaveDaysStatus.REJECTED,
              ELeaveDaysStatus.PENDING,
            ]?.map((type) => {
              return (
                <MenuItem key={type} value={type}>
                  {t(`leaveDays.status.${type}`)}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

export default LeaveDayFormModalBody;
